import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Tooltip, Space, message } from 'antd';
import { PrinterOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { fetchWorkOrderReport } from '../../slices/work-order';

import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const PrintCustomerWorkOrderReport = () => {
  const { id } = useParams();
  const [orderLoading, setOrderLoading] = useState(false);
  const dispatch = useDispatch();
  const [customer, setCustomer] = useState(null);
  const [orderData, setOrderData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);

  const fetchWorkOrderData = useCallback(
    (customerId = id) => {
      setOrderLoading(true);

      const params = {
        customer_id: customerId,
      };

      dispatch(fetchWorkOrderReport(params))
        .then(response => {
          setCustomer(response?.payload?.data[0]?.customer_name);
          const mergedData = () => {
            const grouped = {};
            response?.payload?.data.forEach(item => {
              const key = `${item.workorder_no}-${item.po_no}`;
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(item);
            });

            const result = [];
            Object.values(grouped).forEach(group => {
              group.forEach((row, index) => {
                result.push({
                  ...row,
                  _rowKey: `${row.workorder_no}-${row.po_no}-${row.section}-${index}`,
                  workorder_rowSpan: index === 0 ? group.length : 0,
                  po_rowSpan: index === 0 ? group.length : 0,
                  totalKg_rowSpan: index === 0 ? group.length : 0,
                });
              });
            });

            return result;
          };
          let finalResult = mergedData();
          setOrderData(finalResult);
          setSummaryData(response?.payload?.total_summary);
        })
        .catch(error => {
          console.error('Error fetching work order report:', error);
          setOrderData([]);
          message.error('Failed to fetch work order report');
        })
        .finally(() => {
          setOrderLoading(false);
        });
    }, [dispatch, id],
  );

  useEffect(() => {
    fetchWorkOrderData();
  }, [fetchWorkOrderData]);

  const workOrderColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'sr_no',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
      width: '2%',
    },
    {
      title: 'IWO No / Date',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      width: '4%',
      render: (value, row, index) => {
        const rowSpan = row.workorder_rowSpan;
        if (rowSpan === 0) {
          return {
            children: value,
            props: {
              rowSpan: 0,
            },
          };
        }
        return {
          children: value,
          props: {
            rowSpan: rowSpan,
          },
        };
      },
    },
    {
      title: 'PO NO / Date',
      dataIndex: 'po_no',
      key: 'po_no',
      width: '4%',
      render: (value, row, index) => {
        const rowSpan = row.po_rowSpan;
        if (rowSpan === 0) {
          return {
            children: value,
            props: {
              rowSpan: 0,
            },
          };
        }
        return {
          children: value,
          props: {
            rowSpan: rowSpan,
          },
        };
      },
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      key: 'section',
      width: '3%',
      align: 'center',
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      width: '3%',
      align: 'center',
      render: profile_image => (
        <img
          width="48"
          src={profile_image}
          alt="Section"
          style={{
            width: 90,
            height: 90,
            objectFit: 'contain',
            maxHeight: '100%',
          }}
        />
      ),
    },
    {
      title: 'Customer Ref No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: '3%',
      align: 'center',
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      width: '3%',
      align: 'center',
    },
    {
      title: 'Alloy / Temper',
      dataIndex: 'alloy_temper',
      key: 'alloy_temper',
      width: '3%',
      align: 'center',
    },
    {
      title: 'Order',
      children: [
        {
          title: 'PCS',
          dataIndex: 'order_pcs',
          key: 'order_pcs',
          width: '2%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'order_kgs',
          key: 'order_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Packed',
      children: [
        {
          title: 'PCS',
          dataIndex: 'packed_pcs',
          key: 'packed_pcs',
          width: '2%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'packed_kgs',
          key: 'packed_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Warehouse',
      children: [
        {
          title: 'PCS',
          dataIndex: 'warehouse_pcs',
          key: 'warehouse_pcs',
          width: '2%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'warehouse_kgs',
          key: 'warehouse_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Dispatched',
      children: [
        {
          title: 'PCS',
          dataIndex: 'dispatched_pcs',
          key: 'dispatched_pcs',
          width: '2%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'dispatched_kgs',
          key: 'dispatched_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Pending',
      children: [
        {
          title: 'PCS',
          dataIndex: 'pending_pcs',
          key: 'pending_pcs',
          width: '2%',
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'pending_kgs',
          key: 'pending_kgs',
          width: '3%',
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Total KG',
      dataIndex: 'total_kgs',
      key: 'total_kgs',
      width: '2.5%',
      ellipsis: true,
      render: (value, row, index) => {
        const rowSpan = row.totalKg_rowSpan;
        if (rowSpan === 0) {
          return {
            children: value,
            props: {
              rowSpan: 0,
            },
          };
        }
        return {
          children: value,
          props: {
            rowSpan: rowSpan,
          },
        };
      },
    },
    {
      title: 'Remarks',
      dataIndex: 'remark',
      key: 'remark',
      width: '3.5%',
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <span style={{ whiteSpace: 'pre-wrap' }}>{text || ''}</span>
        </Tooltip>
      ),
    },
  ];

  const generateTableRows = () => {
    return orderData
      .map((order, index) => {
        const isWorkOrderRepeated =
          index > 0 && orderData[index - 1].workorder_no === order.workorder_no;
        const workOrderCell = isWorkOrderRepeated
          ? ''
          : `
        <td rowspan="${(() => {
            let rowSpan = 1;
            for (let i = index + 1; i < orderData.length; i++) {
              if (orderData[i].workorder_no === order.workorder_no) {
                rowSpan++;
              } else {
                break;
              }
            }
            return rowSpan;
          })()}">
          ${order.workorder_no}
          <br/>
          ${order.order_date ? dayjs(order.order_date).format('DD-MM-YYYY') : ''}
        </td>
      `;

        const isPoNoRepeated =
          index > 0 &&
          orderData[index - 1].workorder_no === order.workorder_no &&
          orderData[index - 1].po_no === order.po_no;
        const poNoCell = isPoNoRepeated
          ? ''
          : `
        <td rowspan="${(() => {
            let rowSpan = 1;
            for (let i = index + 1; i < orderData.length; i++) {
              if (
                orderData[i].workorder_no === order.workorder_no &&
                orderData[i].po_no === order.po_no
              ) {
                rowSpan++;
              } else {
                break;
              }
            }
            return rowSpan;
          })()}">
          ${order.po_no || ''}
          <br/>
          ${order.po_date ? dayjs(order.po_date).format('DD-MM-YYYY') : ''}
        </td>
      `;

        const isTotalKgRepeated =
          index > 0 &&
          orderData[index - 1].workorder_no === order.workorder_no &&
          orderData[index - 1].total_kgs === order.total_kgs;

        const totalKgCells = isTotalKgRepeated
          ? ''
          : `
    <td rowspan="${(() => {
            let rowSpan = 1;
            for (let i = index + 1; i < orderData.length; i++) {
              if (
                orderData[i].workorder_no === order.workorder_no &&
                orderData[i].po_no === order.po_no
              ) {
                rowSpan++;
              } else {
                break;
              }
            }
            return rowSpan;
          })()}">
      ${order.total_kgs || ''}   <!-- total KGS -->
    </td>
  `;


        return `
  <tr>
    <td>${index + 1}</td>
    ${workOrderCell}
    ${poNoCell}
    <td>${order.section || 'N/A'}</td>
    <td>
      ${order.profile_image && order.profile_image !== 'N/A'
            ? `<img src="${order.profile_image}" alt="Profile" class="profile-image" onerror="this.style.display='none';" />`
            : ' '}
    </td>
    <td>${order.customer_reference_number || ''}</td>
    <td>${order.length || 'N/A'}</td>
    <td>${order.alloy_temper || 'N/A'}</td>
    <td>${order.order_pcs || ''}</td>
    <td style="text-align: right;">${order.order_kgs ? parseFloat(order.order_kgs).toFixed(3) : ''}</td>
    <td>${order.packed_pcs || ''}</td>
    <td style="text-align: right;">${order.packed_kgs ? parseFloat(order.packed_kgs).toFixed(3) : ''}</td>
    <td>${order.warehouse_pcs || ''}</td>
    <td style="text-align: right;">${order.warehouse_kgs ? parseFloat(order.warehouse_kgs).toFixed(3) : ''}</td>
    <td>${order.dispatched_pcs || ''}</td>
    <td style="text-align: right;">${order.dispatched_kgs ? parseFloat(order.dispatched_kgs).toFixed(3) : ''}</td>
    <td>${order.pending_pcs || ''}</td>
    <td style="text-align: right;">${order.pending_kgs ? parseFloat(order.pending_kgs).toFixed(3) : ''}</td>
     ${totalKgCells}
  <td>${order.remark || ''}</td>
  </tr>
`;

      })
      .join('');
  };

  const handlePrint = () => {
    const windowPrint = window.open('', '', 'width=1200,height=900');

    windowPrint.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Work Order Report - ${customer || 'Customer'}</title>
      <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 9px;
            line-height: 1.2;
            color: #333;
            background: white;
            margin: 0;
            padding: 0;
          }
          
          .print-container {
            padding: 5px;
            max-width: 100%;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 10px;
            padding: 5px 0;
          }
          
          .print-header h1 {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #333;
          }
          
          .print-header h2 {
            font-size: 14px;
            color: #666;
            margin-bottom: 0;
          }
          
          .work-order-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 5px;
            font-size: 8px;
            border: 0.5px solid #333;
          }
          
          .work-order-table th,
          .work-order-table td {
            border: 1px solid #333;
            padding: 1px 2px;
            text-align: center;
            vertical-align: middle;
            word-wrap: break-word;
          }
          
          .work-order-table th {
            background-color: #12406a;
            font-weight: bold;
            font-size: 8px;
            text-align: center;
            color: white;
            border: 1px solid #333;
            padding: 2px 3px;
          }
          
          .work-order-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .total-row {
            font-weight: bold;
            font-size: 8px;
            border-top: 1px solid #333 !important;
          }
          
          .total-row td {
            border-top: 1px solid #333 !important;
            font-weight: bold;
            padding: 2px 3px;
          }
          
          .profile-image {
            max-width: 40px;
            max-height: 30px;
            display: block;
            margin: 0 auto;
            object-fit: contain;
          }
          
          /* Specific column width controls for Portrait A4 */
          .work-order-table colgroup col:nth-child(1) { width: 3%; }   /* Sr. No */
          .work-order-table colgroup col:nth-child(2) { width: 7%; }   /* Work Order */
          .work-order-table colgroup col:nth-child(3) { width: 7%; }   /* PO No */
          .work-order-table colgroup col:nth-child(4) { width: 5%; }   /* Profile No */
          .work-order-table colgroup col:nth-child(5) { width: %6; }   /* Profile Image */
          .work-order-table colgroup col:nth-child(6) { width: 7%; }   /* Customer Ref */
          .work-order-table colgroup col:nth-child(7) { width: 5%; }   /* Length */
          .work-order-table colgroup col:nth-child(8) { width: 4%; }   /* Order PCS */
          .work-order-table colgroup col:nth-child(9) { width: 4%; }   /* Order KGS */
          .work-order-table colgroup col:nth-child(10) { width: 4%; }  /* Dispatched PCS */
          .work-order-table colgroup col:nth-child(11) { width: 4%; }  /* Dispatched KGS */
          .work-order-table colgroup col:nth-child(12) { width: 4%; }  /* Packed PCS */
          .work-order-table colgroup col:nth-child(13) { width: 4%; }  /* Packed KGS */
          .work-order-table colgroup col:nth-child(14) { width: 4%; }  /* Warehouse PCS */
          .work-order-table colgroup col:nth-child(15) { width: 4%; }  /* Warehouse KGS */
          .work-order-table colgroup col:nth-child(16) { width: 4%; }  /* Pending PCS */
          .work-order-table colgroup col:nth-child(17) { width: 4%; }  /* Pending KGS */
          .work-order-table colgroup col:nth-child(18) { width: 5%; }  /* Remarks */
          
          @media print {
            body { 
              font-size: 8px; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              margin: 0;
              padding: 0;
            }
            .print-container { 
              padding: 8px !important; 
              margin: 0;
            }
            .work-order-table { 
              font-size: 8px; 
              margin-top: 5px;
            }
            .work-order-table th,
            .work-order-table td { 
              padding: 1px 2px; 
              font-size: 8px;
            }
            .profile-image {
              max-width: 30px;
              max-height: 20px;
            }
            .print-header h1 {
              font-size: 14px;
              margin-bottom: 3px;
            }
            .print-header h2 {
              font-size: 12px;
              margin-bottom: 0;
            }
          }
          
          @page {
            margin: 0.2in;
            size: A4 portrait;
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="print-header">
            <h1>Work Order Report</h1>
            <h2>${customer || 'N/A'}</h2>
          </div>
          
          <table class="work-order-table">
            <thead>
              <tr>
                <th rowspan="2">Sr.<br/>No.</th>
                <th rowspan="2">Work Order No/<br/>Date</th>
                <th rowspan="2">PO NO/<br/>Date</th>
                <th rowspan="2">Profile<br/>No</th>
                <th rowspan="2">Profile<br/>Image</th>
                <th rowspan="2">Customer Reference<br/>Number</th>
                <th rowspan="2">Length<br/>(mm)</th>
                <th rowspan="2">Alloy<br/>Temper</th>
                <th colspan="2">Order</th>
                <th colspan="2">Packed</th>
                <th colspan="2">Warehouse</th>
                <th colspan="2">Dispatched</th>
                <th colspan="2">Pending</th>
                <th rowspan="2">Total KG</th>
                <th rowspan="2">Remarks</th>
              </tr>
              <tr>
                <th>PCS</th>
                <th>KGS</th>
                <th>PCS</th>
                <th>KGS</th>
                <th>PCS</th>
                <th>KGS</th>
                <th>PCS</th>
                <th>KGS</th>
                <th>PCS</th>
                <th>KGS</th>
              </tr>
            </thead>
            <tbody>
              ${generateTableRows()}
              ${orderData.length > 0
        ? `
                <tr class="total-row">
                  <td colspan="8"><strong>Total</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseInt(order.order_pcs) || 0), 0)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseFloat(order.order_kgs) || 0), 0).toFixed(3)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseInt(order.packed_pcs) || 0), 0)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseFloat(order.packed_kgs) || 0), 0).toFixed(3)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseInt(order.warehouse_pcs) || 0), 0)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseFloat(order.warehouse_kgs) || 0), 0).toFixed(3)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseInt(order.dispatched_pcs) || 0), 0)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseFloat(order.dispatched_kgs) || 0), 0).toFixed(3)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseInt(order.pending_pcs) || 0), 0)}</strong></td>
                  <td><strong>${orderData.reduce((sum, order) => sum + (parseFloat(order.pending_kgs) || 0), 0).toFixed(3)}</strong></td>
                  <td></td>
                </tr>
              `
        : ''
      }
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `);

    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 500); // Increased timeout to allow images to load
  };

  const handleExcelDownload = () => {
    const headers = [
      'Sr. No.',
      'IWO No / Date',
      'PO No / Date',
      'Profile No',
      'Customer Reference Number',
      'Length (mm)',
      'Alloy / Temper',
      'Order PCS',
      'Order KGS',
      'Packed PCS',
      'Packed KGS',
      'Warehouse PCS',
      'Warehouse KGS',
      'Dispatched PCS',
      'Dispatched KGS',
      'Pending PCS',
      'Pending KGS',
      'Total KG',
      'Remarks',
    ];

    // Prepare the data rows
    const dataRows = orderData?.map((order, index) => {
      const workOrderCell = `${order?.workorder_no || ''}${order?.order_date ? '\n' + dayjs(order?.order_date).format('DD-MM-YYYY') : ''}`;
      const poNoCell = `${order?.po_no || ''}${order?.po_date ? '\n' + dayjs(order.po_date).format('DD-MM-YYYY') : ''}`;
      const totalKgCell = `${order?.total_kgs ? parseFloat(order?.total_kgs).toFixed(3) : ''}`;

      return [
        index + 1,
        workOrderCell,
        poNoCell,
        order.section || 'N/A',
        order.customer_reference_number || '',
        Number(order.length) || 'N/A',
        order?.alloy_temper,
        Number(order.order_pcs) || '',
        order.order_kgs ? Number(parseFloat(order.order_kgs)) : 0,
        Number(order.packed_pcs) || '',
        order.packed_kgs ? Number(parseFloat(order.packed_kgs)) : 0,
        Number(order.warehouse_pcs) || '',
        order.warehouse_kgs ? Number(parseFloat(order.warehouse_kgs)) : 0,
        Number(order.dispatched_pcs) || '',
        order.dispatched_kgs ? Number(parseFloat(order.dispatched_kgs)) : 0,
        Number(order.pending_pcs) || '',
        order.pending_kgs ? Number(parseFloat(order.pending_kgs)) : 0,
        Number(totalKgCell),
        order.remark || '',
      ];
    });

    // Calculate totals for numeric columns
    const totalRow = [
      'TOTAL', // Sr. No.
      '', // Work Order No / Date
      '', // PO No / Date
      '', // Profile No
      '', // Customer Reference Number
      '', // Length (mm)
      '',
      //   summaryData?.order_pcs, // Order PCS
      //   summaryData?.order_kgs, // Order KGS
      //   summaryData?.packed_pcs, // Packed PCS
      //   summaryData?.packed_kgs, // Packed KGS
      //   summaryData?.warehouse_pcs, // Warehouse PCS
      //   summaryData?.warehouse_kgs, // Warehouse KGS
      //   summaryData?.dispatched_pcs, // Dispatched PCS
      //   summaryData?.dispatched_kgs, // Dispatched KGS
      //   summaryData?.pending_pcs, // Pending PCS
      //   summaryData?.pending_kgs, // Pending KGS

      { f: `SUM(H2:H${dataRows.length + 1})` }, // Order KGS
      { f: `SUM(I2:I${dataRows.length + 1})` }, // Packed PCS
      { f: `SUM(J2:J${dataRows.length + 1})` }, // Packed KGS
      { f: `SUM(K2:K${dataRows.length + 1})` }, // Warehouse PCS
      { f: `SUM(L2:L${dataRows.length + 1})` }, // Warehouse KGS
      { f: `SUM(M2:M${dataRows.length + 1})` }, // Dispatched PCS
      { f: `SUM(N2:N${dataRows.length + 1})` }, // Dispatched KGS
      { f: `SUM(O2:O${dataRows.length + 1})` }, // Pending PCS
      { f: `SUM(P2:P${dataRows.length + 1})` }, // Pending KGS
      { f: `SUM(Q2:Q${dataRows.length + 1})` }, // Order PCS
      '',
      '', // Remarks
    ];

    // Combine headers, data rows, and total row
    const wsData = [headers, ...dataRows, totalRow];

    // Create a new workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Define merges array for cell merging
    const merges = [];

    // Group and merge logic for "Work Order No / Date" (column B, index 1)
    let workOrderStartRow = 1; // Start from row 1 (after header)
    for (let i = 1; i < dataRows.length; i++) {
      const currentWorkOrder = dataRows[i][1];
      const previousWorkOrder = dataRows[i - 1][1];
      if (currentWorkOrder !== previousWorkOrder) {
        if (i - workOrderStartRow > 0) {
          merges.push({
            s: { r: workOrderStartRow, c: 1 }, // Start row and column (B)
            e: { r: i, c: 1 }, // End row and column (B)
          });
        }
        workOrderStartRow = i + 1;
      }
    }
    // Handle the last group of Work Order
    if (dataRows.length - workOrderStartRow > 0) {
      merges.push({
        s: { r: workOrderStartRow, c: 1 },
        e: { r: dataRows.length, c: 1 },
      });
    }

    // Group and merge logic for "PO No / Date" (column C, index 2)
    let poNoStartRow = 1;
    for (let i = 1; i < dataRows.length; i++) {
      const currentPoNo = dataRows[i][2];
      const previousPoNo = dataRows[i - 1][2];
      const currentWorkOrder = dataRows[i][1];
      const previousWorkOrder = dataRows[i - 1][1];
      if (
        currentPoNo !== previousPoNo ||
        currentWorkOrder !== previousWorkOrder
      ) {
        if (i - poNoStartRow > 0) {
          merges.push({
            s: { r: poNoStartRow, c: 2 }, // Start row and column (C)
            e: { r: i, c: 2 }, // End row and column (C)
          });
        }
        poNoStartRow = i + 1;
      }
    }
    // Handle the last group of PO No
    if (dataRows.length - poNoStartRow > 0) {
      merges.push({
        s: { r: poNoStartRow, c: 2 },
        e: { r: dataRows.length, c: 2 },
      });
    }

    /* Total KG Cell Merge Logic */
    let totalKgStartRow = 1; // Start from first data row (after header)
    for (let i = 1; i < dataRows.length; i++) {
      const currentTotalKg = dataRows[i][17];
      const previousTotalKg = dataRows[i - 1][17];
      if (currentTotalKg !== previousTotalKg) {
        if (i - totalKgStartRow > 0) {
          merges.push({
            s: { r: totalKgStartRow, c: 17 }, // Start row and column (Q)
            e: { r: i, c: 17 }, // End row and column (Q)
          });
        }
        totalKgStartRow = i + 1;
      }
    }
    // Handle the last group of Total KG
    if (dataRows.length - totalKgStartRow >= 0) {
      merges.push({
        s: { r: totalKgStartRow, c: 17 },
        e: { r: dataRows.length, c: 17 },
      });
    }

    // Apply merges to the worksheet
    ws['!merges'] = merges;
    ['I', 'K', 'M', 'O', 'Q'].forEach(col => {
      for (let row = 2; row <= dataRows.length + 2; row++) {
        const cellAddress = `${col}${row}`;
        if (ws[cellAddress]) {
          ws[cellAddress].z = '0.000'; // 3 decimal places
        }
      }
    });

    merges.forEach(merge => {
      const cellAddress = XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c });

      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          alignment: {
            vertical: 'center',
            horizontal: 'center',
            wrapText: true,
          },
        };
      }
    });

    // Adjust column widths
    ws['!cols'] = [
      { wch: 10 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 }, // Profile No
      { wch: 20 }, // Customer Reference Number
      { wch: 15 }, // Length (mm)
      { wch: 10 }, // Order PCS
      { wch: 10 }, // Order KGS
      { wch: 10 }, // Dispatched PCS
      { wch: 10 }, // Dispatched KGS
      { wch: 10 }, // Packed PCS
      { wch: 10 }, // Packed KGS
      { wch: 10 }, // Warehouse PCS
      { wch: 10 }, // Warehouse KGS
      { wch: 10 }, // Pending PCS
      { wch: 10 }, // Pending KGS
      { wch: 15 }, // Remarks
    ];

    // // Apply middle alignment (vertical and horizontal) to all cells

    for (let row = 0; row < wsData.length; row++) {
      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;
        const isNumericColumn = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15].includes(
          col,
        );
        const isCenterColumn = [1, 2, 16, 17].includes(col); // Example: Work Order, PO, Total KG, Remarks

        ws[cellAddress].s = {
          alignment: {
            vertical: 'center',
            horizontal: isCenterColumn
              ? 'center'
              : isNumericColumn
                ? 'right'
                : 'left',
            wrapText: true,
          },
        };
      }
    }

    // Create a workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Work Order Report');

    // Generate the Excel file and trigger download
    XLSX.writeFile(wb, `${customer}_Work_Order_Report.xlsx`);
  };

  return (
    <div style={{ padding: '20px' }} className="print-container">
      <div
        className="print-header"
        style={{ marginBottom: '20px', textAlign: 'center' }}
      >
        <h1>Work Order Report</h1>
        <h3>{customer}</h3>
      </div>

      <div
        className="action-buttons"
        style={{ marginBottom: '20px', textAlign: 'right' }}
      >
        <Space size="large">
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            size="large"
            loading={orderLoading}
          >
            Print
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExcelDownload}
            size="large"
            loading={orderLoading}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      <div className="table-wrapper">
        <Table
          rowKey="_rowKey"
          dataSource={orderData}
          columns={workOrderColumns}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: 'max-content' }}
          summary={() => {
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                  <Table.Summary.Cell key="sum-label" index={0} colSpan={8}>
                    Total
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-order-pcs" index={6} align="center">
                    {summaryData?.order_pcs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-order-kgs" index={7} align="right">
                    {summaryData?.order_kgs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-packed-pcs" index={8} align="center">
                    {summaryData?.packed_pcs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-packed-kgs" index={9} align="right">
                    {summaryData?.packed_kgs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-warehouse-pcs" index={10} align="center">
                    {summaryData?.warehouse_pcs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-warehouse-kgs" index={11} align="right">
                    {summaryData?.warehouse_kgs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-dispatched-pcs" index={12} align="center">
                    {summaryData?.dispatched_pcs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-dispatched-kgs" index={13} align="right">
                    {summaryData?.dispatched_kgs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-pending-pcs" index={14} align="center">
                    {summaryData?.pending_pcs}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-pending-kgs" index={15} align="right">
                    {summaryData?.pending_kgs?.toFixed(3)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell key="sum-empty" index={16}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </div>

      <style>{`
        @media print {
          /* Hide non-printable elements */
          .action-buttons,
          .ant-btn,
          .ant-tooltip,
          .ant-dropdown,
          .ant-menu {
            display: none !important;
          }

          /* Reset page margins and padding */
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }

          /* Container styling */
          .print-container {
            padding: 5px !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: none !important;
          }

          /* Header styling */
          .print-header {
            page-break-inside: avoid;
            margin-bottom: 15px !important;
            text-align: center;
          }

          .print-header h1 {
            margin: 0 0 8px 0 !important;
            font-size: 18px !important;
            font-weight: bold !important;
            color: #000 !important;
          }

          .print-header p {
            margin: 0 !important;
            font-size: 12px !important;
            color: #000 !important;
          }

          /* Table wrapper */
          .table-wrapper {
            width: 100% !important;
            overflow: visible !important;
          }

          /* Table styling */
          .ant-table {
            font-size: 9px !important;
            width: 100% !important;
            margin: 0 !important;
            page-break-inside: auto;
          }

          .ant-table table {
            width: 100% !important;
            border-collapse: collapse !important;
            border-spacing: 0 !important;
          }

          /* Table header styling */
          .ant-table-thead > tr > th {
            background: #12406a !important;
            color: white !important;
            padding: 4px 2px !important;
            font-size: 11px !important;
            font-weight: bold !important;
            text-align: center !important;
            page-break-inside: avoid;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Table body styling */
          .ant-table-tbody > tr > td {
            padding: 2px 1px !important;
            font-size: 11px !important;
            word-wrap: break-word !important;
            text-align: center !important;
            overflow-wrap: break-word !important;
            page-break-inside: avoid;
            border: 1px solid #999 !important;
          }

          /* Image styling in print */
          .ant-table-tbody img {
            max-width: 40px !important;
            max-height: 30px !important;
            object-fit: contain !important;
          }

          /* Remove table scroll */
          .ant-table-body {
            overflow: visible !important;
          }

          .ant-table .ant-table-container {
            overflow: visible !important;
          }

          /* Page break settings */
          .ant-table-tbody > tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Ensure proper table layout */
          .ant-table-content {
            overflow: visible !important;
          }

          /* Force table to use full width */
          .ant-table-scroll {
            overflow: visible !important;
          }

          /* Remove any flex or grid layouts that might interfere */
          .ant-table-header,
          .ant-table-body,
          .ant-table-content {
            display: block !important;
          }

          /* Page settings */
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
        }

        /* Screen styles for better visibility */
        .table-wrapper {
          overflow-x: auto;
        }

        .ant-table {
          min-width: 1200px;
        }
      `}</style>
    </div>
  );
};

export default PrintCustomerWorkOrderReport;
